import { useCallback, useState, useEffect, useRef } from 'react'
import { UseFormReturn } from 'react-hook-form'
import {
    injectTourStyles,
    cleanupTourElements,
    createTourPopover,
    positionPopover,
    createTourOverlay,
} from '@/utils/tour-styles'
import type { CustomerCreateFormValues } from '@/modules/Customer/types'

interface TourStep {
    selector: string
    title: string
    description: string
    formStep?: number
    setValue?: () => void
}

interface UseCustomerCreateTourOptions {
    methods: UseFormReturn<CustomerCreateFormValues>
    setCurrentStep: (step: number) => void
}

/**
 * Tour hook for Customer Create V2 form
 * Uses shared tour-styles for consistent styling
 */
export const useCustomerCreateTour = ({
    methods,
    setCurrentStep,
}: UseCustomerCreateTourOptions) => {
    const [isActive, setIsActive] = useState(false)
    const [_currentTourStep, setCurrentTourStep] = useState(0)
    let currentFormStep = 1

    // Store options in refs to avoid re-creating callbacks
    const methodsRef = useRef(methods)
    const setCurrentStepRef = useRef(setCurrentStep)
    methodsRef.current = methods
    setCurrentStepRef.current = setCurrentStep

    const tourSteps: TourStep[] = [
        // Step 1: Email check
        {
            selector: '[data-tour="customer-form"]',
            title: 'Tạo khách hàng mới',
            description: 'Quy trình gồm 4 bước để tạo khách hàng mới.',
            formStep: 1,
        },
        {
            selector: '[data-tour="email-input"]',
            title: 'Kiểm tra Email',
            description:
                'Nhập email khách hàng. Hệ thống sẽ kiểm tra email đã tồn tại chưa.',
            formStep: 1,
            setValue: () =>
                methodsRef.current.setValue(
                    'customer.customer_email',
                    'demo@example.com'
                ),
        },
        // Step 2: Customer info
        {
            selector: '[data-tour="customer-type"]',
            title: 'Loại khách hàng',
            description: 'Chọn loại khách hàng: Cá nhân hoặc Doanh nghiệp.',
            formStep: 2,
        },
        {
            selector: '[data-tour="industry-select"]',
            title: 'Lĩnh vực',
            description: 'Chọn lĩnh vực hoạt động của khách hàng.',
            formStep: 2,
            setValue: () =>
                methodsRef.current.setValue(
                    'customer.customer_industry',
                    'technology'
                ),
        },
        {
            selector: '[data-tour="customer-source"]',
            title: 'Nguồn khách hàng',
            description:
                'Chọn nguồn tiếp cận khách hàng: Nghiên cứu, Sự kiện, Giới thiệu...',
            formStep: 2,
        },
        {
            selector: '[data-tour="service-select"]',
            title: 'Dịch vụ quan tâm',
            description: 'Chọn các dịch vụ khách hàng đang quan tâm.',
            formStep: 2,
        },
        // Step 3: Contact info
        {
            selector: '[data-tour="customer-name"]',
            title: 'Tên khách hàng',
            description: 'Nhập họ tên người liên hệ chính.',
            formStep: 3,
            setValue: () =>
                methodsRef.current.setValue(
                    'customer.customer_name',
                    'Nguyễn Văn A'
                ),
        },
        {
            selector: '[data-tour="customer-phone"]',
            title: 'Số điện thoại',
            description: 'Nhập số điện thoại liên hệ.',
            formStep: 3,
            setValue: () =>
                methodsRef.current.setValue(
                    'customer.customer_phone',
                    '0901234567'
                ),
        },
        {
            selector: '[data-tour="customer-position"]',
            title: 'Chức vụ',
            description: 'Nhập chức vụ của người liên hệ.',
            formStep: 3,
            setValue: () =>
                methodsRef.current.setValue(
                    'customer.customer_position',
                    'Manager'
                ),
        },
        // Step 4: Submit
        {
            selector: '[data-tour="submit-btn"]',
            title: 'Hoàn thành',
            description: 'Nhấn nút để tạo khách hàng mới.',
            formStep: 4,
        },
    ]

    const showTourUI = (step: TourStep, stepIndex: number) => {
        injectTourStyles()
        cleanupTourElements()
        createTourOverlay()

        const element = document.querySelector(step.selector) as HTMLElement
        if (!element) {
            // Skip to next step if element not found
            if (stepIndex < tourSteps.length - 1) {
                goToStep(stepIndex + 1)
            } else {
                endTour()
            }
            return
        }

        element.classList.add('tour-highlight-element')
        element.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // Wait for scroll then add popover
        setTimeout(() => {
            const popover = createTourPopover({
                title: step.title,
                description: step.description,
                currentStep: stepIndex + 1,
                totalSteps: tourSteps.length,
                onPrev:
                    stepIndex > 0 ? () => goToStep(stepIndex - 1) : undefined,
                onNext: () => {
                    if (stepIndex === tourSteps.length - 1) {
                        endTour()
                    } else {
                        goToStep(stepIndex + 1)
                    }
                },
                onClose: endTour,
                isLastStep: stepIndex === tourSteps.length - 1,
            })

            document.body.appendChild(popover)
            positionPopover(popover, element)
        }, 300)
    }

    const goToStep = (stepIndex: number) => {
        const step = tourSteps[stepIndex]
        if (!step) return

        cleanupTourElements()

        // Change form step if needed
        if (step.formStep && step.formStep !== currentFormStep) {
            currentFormStep = step.formStep
            setCurrentStepRef.current(step.formStep)
            // Wait for DOM update
            setTimeout(() => {
                step.setValue?.()
                showTourUI(step, stepIndex)
                setCurrentTourStep(stepIndex)
            }, 400)
        } else {
            step.setValue?.()
            showTourUI(step, stepIndex)
            setCurrentTourStep(stepIndex)
        }
    }

    const endTour = useCallback(() => {
        cleanupTourElements()
        setIsActive(false)
        setCurrentTourStep(0)
        methodsRef.current.reset()
        setCurrentStepRef.current(1)
    }, [])

    const startDemoTour = useCallback(() => {
        methodsRef.current.reset()
        setCurrentStepRef.current(1)
        setIsActive(true)
        setCurrentTourStep(0)
        currentFormStep = 1

        window.scrollTo({ top: 0, behavior: 'smooth' })

        setTimeout(() => {
            const firstStep = tourSteps[0]
            firstStep.setValue?.()
            showTourUI(firstStep, 0)
        }, 500)
    }, [])

    useEffect(() => {
        return () => {
            cleanupTourElements()
        }
    }, [])

    return { startDemoTour, endTour, isActive }
}

export default useCustomerCreateTour
